import { Text as BPText } from "@blueprintjs/core";
import { toUpperCase } from "@hex/common";
import React from "react";
import styled, { css } from "styled-components";

import { monoTextCss } from "../theme/common/sharedThemeStyles.js";
import { Theme } from "../theme/common/theme.js";

export type FontSize = Lowercase<
  "EXTRA_SMALL" | "SMALL" | "DEFAULT" | "LARGE" | "EXTRA_LARGE"
>;
export type FontColor = Lowercase<keyof Theme["fontColor"]>;
export type FontWeight = Lowercase<keyof Theme["fontWeight"]>;
export type FontFamily = Lowercase<keyof Theme["fontFamily"]>;
export type MonoVariant = "default" | "small";

interface TxtInternalProps {
  $fontSize: FontSize | undefined;
  $fontColor: FontColor | undefined;
  $fontWeight: FontWeight | undefined;
  $fontFamily: FontFamily | undefined;
  $mono: MonoVariant | undefined;
  $strikethrough: boolean | undefined;
}

export interface TxtProps extends React.HTMLAttributes<HTMLElement> {
  /** optional: the semantic html element to render as*/
  renderAs?: keyof React.JSX.IntrinsicElements;
  fontSize?: FontSize;
  fontColor?: FontColor;
  fontWeight?: FontWeight;
  fontFamily?: FontFamily;
  /**
   * Applies monospace font with an optical size adjustment so mono text
   * aligns visually with surrounding sans-serif text. Overrides
   * `fontFamily` and `fontSize` when set.
   * - `"default"` → 13px mono (matches 14px sans)
   * - `"small"` → 11px mono (matches 12px sans)
   */
  mono?: MonoVariant;
  /**
   * Blueprint uses the `title` prop to display a default tooltip.
   * Use disableTooltip if you want to wrap the tag into a HexTooltip, and do not want duplicate tooltips on the Txt.
   */
  disableTooltip?: boolean;
  ellipsize?: boolean;
  strikethrough?: boolean;

  children?: React.ReactNode;

  // Extra standard props to pass straight through
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  onClick?: React.MouseEventHandler;
}

export const Txt = ({
  children,
  disableTooltip = false,
  ellipsize = false,
  fontColor,
  fontFamily,
  fontSize,
  fontWeight,
  mono,
  ref,
  renderAs,
  strikethrough = false,
  title,
  ...rest
}: TxtProps & { ref?: React.Ref<HTMLElement> }) => {
  return ellipsize ? (
    // We switch to blueprint's text component only if you want to use the ellipses prop since it's a more expensive component to render
    <StyledBPText
      ref={ref}
      $fontColor={fontColor}
      $fontFamily={fontFamily}
      $fontSize={fontSize}
      $fontWeight={fontWeight}
      $mono={mono}
      $strikethrough={strikethrough}
      ellipsize={true}
      // Cannot ellipses on an inline element like span https://semicolon.dev/tutorial/css/text-overflow-ellipsis-doesnt-work
      // We only use this component when ellipsize = true, so default to div instead of span
      tagName={renderAs ?? "div"}
      // Remove the default HTML tooltip prop if the prop is set to true. Note that the title must equal "", which is treated differently than undefined.
      title={disableTooltip ? "" : title}
      {...rest}
    >
      {children}
    </StyledBPText>
  ) : (
    <TxtInternal
      ref={ref}
      $fontColor={fontColor}
      $fontFamily={fontFamily}
      $fontSize={fontSize}
      $fontWeight={fontWeight}
      $mono={mono}
      $strikethrough={strikethrough}
      as={renderAs ?? "span"}
      title={disableTooltip ? "" : title}
      {...rest}
    >
      {children}
    </TxtInternal>
  );
};

const SharedCSS = css<TxtInternalProps>`
  font-size: ${({ $fontSize, theme }) =>
    $fontSize ? theme.fontSize[toUpperCase($fontSize)] : "inherit"};
  color: ${({ $fontColor, theme }) =>
    $fontColor ? theme.fontColor[toUpperCase($fontColor)] : "inherit"};
  font-weight: ${({ $fontWeight, theme }) =>
    $fontWeight ? theme.fontWeight[toUpperCase($fontWeight)] : "inherit"};
  /* Line-height is not configurable on this component - always in lockstep with fontSize */
  line-height: ${({ $fontSize, theme }) =>
    $fontSize ? theme.lineHeight[toUpperCase($fontSize)] : "inherit"};
  text-decoration: ${({ $strikethrough }) =>
    $strikethrough ? "line-through" : "none"};
  font-family: ${({ $fontFamily, theme }) =>
    $fontFamily ? theme.fontFamily[toUpperCase($fontFamily)] : "inherit"};
  ${({ $mono }) => $mono != null && monoTextCss($mono)};
`;

const TxtInternal = styled.span<TxtInternalProps>`
  ${SharedCSS}
`;

const StyledBPText = styled(BPText)<TxtInternalProps>`
  ${SharedCSS}
`;
